// fork of use-yup
// https://github.com/usereact/use-yup/blob/master/src/index.ts

import { useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { BaseSchema } from 'yup';
import { ValidateOptions } from 'yup/lib/types';
import _mapValues from 'lodash/mapValues';
import _keyBy from 'lodash/keyBy';

type Values = {
  [field: string]: any;
};

type ValidationErrors<T> = {
  [K in keyof T]?: T[K] extends Record<string, unknown> ? ValidationErrors<T[K]> : string;
};

type ValidationResult<T> = {
  errors: ValidationErrors<T>;
  isValid: boolean;
};

type UseYup<T> = {
  validate: () => any;
  errors: ValidationErrors<T>;
  isValid: boolean;
};

function useYup<T extends Values>(
  values: T,
  validationSchema: BaseSchema<T>,
  options: ValidateOptions = {},
): UseYup<T> {
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors<T>>({});
  const isValid = Object.keys(errors).length === 0;

  const validate = () => {
    setDirty(true);

    return validationSchema
      .validate(values, { abortEarly: false, ...options })
      .then(() => {
        return {} as ValidationErrors<T>;
      })
      .catch((error: any) => {
        return yupToValidationErrors<T>(error);
      })
      .then((newErrors: ValidationErrors<T>) => {
        setErrors(newErrors);
        return {
          errors: newErrors,
          isValid: Object.keys(newErrors).length === 0,
        } as ValidationResult<T>;
      });
  };

  useDeepCompareEffect(() => {
    dirty && validate();
  }, [values]);

  return {
    validate,
    errors,
    isValid,
  };
}

function yupToValidationErrors<T extends Values>(yupError: any): ValidationErrors<T> {
  const byPath = _keyBy(yupError.inner, 'path');
  const errors = _mapValues(byPath, (path) => path.message);
  return errors;
}

export default useYup;
